import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { Card } from "@/components/ui/card";

const COUNT_TONES = {
  primary: "bg-primary/10 text-primary",
  rose: "bg-rose-100 text-rose-700",
  amber: "bg-amber-100 text-amber-700",
  green: "bg-green-100 text-green-700",
  slate: "bg-slate-100 text-slate-600",
};
const DOT_TONES = { green: "bg-green-500", yellow: "bg-amber-400", red: "bg-rose-500", slate: "bg-slate-300" };
const DOT_TITLES = { green: "All complete", yellow: "In progress", red: "Not started", slate: "" };

// A self-contained collapsible section card. The title row toggles open/closed.
// - persistKey: remembers open/closed state in localStorage (per browser/user).
// - count + countTone: a color-coded counter badge in the header (shown when count>0).
// - dot + dotTitle: a small traffic-light dot before the title (status / new-unread).
// - headerInfo: a node always visible in the header (even when collapsed).
// - headerRight: actions shown only when expanded.
// - onOpenChange(open): fired on mount and whenever open changes (used to mark "seen").
export const CollapsibleCard = ({ icon: Icon, dot, dotTitle, title, badge, headerRight, headerInfo, defaultOpen = true, persistKey, count, countTone = "primary", testid, bodyClassName = "", onOpenChange, children }) => {
  const storageKey = persistKey ? `conduit:section:${persistKey}` : null;
  const [open, setOpen] = useState(() => {
    if (storageKey) {
      try { const v = localStorage.getItem(storageKey); if (v !== null) return v === "1"; } catch { /* ignore */ }
    }
    return defaultOpen;
  });
  useEffect(() => { onOpenChange?.(open); }, [open]); // eslint-disable-line
  const toggle = () => setOpen((o) => {
    const next = !o;
    if (storageKey) { try { localStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* ignore */ } }
    return next;
  });
  const hasCount = typeof count === "number" && count > 0;
  const rightSide = headerInfo || (open && headerRight);
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
        <button type="button" onClick={toggle} className="flex min-w-0 items-center gap-2 text-left" data-testid={testid ? `${testid}-toggle` : undefined}>
          <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "" : "-rotate-90"}`} />
          {dot && <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${DOT_TONES[dot] || DOT_TONES.slate}`} data-testid={testid ? `${testid}-dot` : undefined} title={dotTitle || DOT_TITLES[dot] || ""} />}
          {Icon && <Icon className="h-4 w-4 shrink-0 text-primary" />}
          <h3 className="truncate font-heading text-sm font-semibold text-slate-800">{title}</h3>
          {hasCount && (
            <span className={`inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ${COUNT_TONES[countTone] || COUNT_TONES.primary}`} data-testid={testid ? `${testid}-count` : undefined}>{count}</span>
          )}
          {badge}
        </button>
        {rightSide ? <div className="flex shrink-0 items-center gap-2">{headerInfo}{open && headerRight}</div> : null}
      </div>
      {open && <div className={bodyClassName}>{children}</div>}
    </Card>
  );
};

// Small helper: track a per-key "last seen" timestamp and compute unread state.
export const useSeen = (seenKey, latestActivity) => {
  const [seen, setSeen] = useState(() => {
    try { return localStorage.getItem(`conduit:seen:${seenKey}`); } catch { return null; }
  });
  const unread = !!latestActivity && (!seen || seen < latestActivity);
  const markSeen = () => {
    if (!latestActivity) return;
    try { localStorage.setItem(`conduit:seen:${seenKey}`, latestActivity); } catch { /* ignore */ }
    setSeen(latestActivity);
  };
  return { unread, markSeen };
};
